import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { setAppBadge } from '@/lib/push';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, institutionScope, onAuthLost, sandboxReturn, sandboxVisit, tokens, viewAs as viewAsStore, VIEW_AS_END_PATH, type ViewAsState } from '@/lib/api';
import type { EffectiveRole, InstitutionContext, MeResponse, TokenPair, MfaChallenge } from '@/lib/types';
import { roleHasPermission, type Permission } from './permissions';
import { markActive, trustedDevice } from '@/lib/session';

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  me: MeResponse | null;
  /** السياقات التي يملك المستخدم ارتباطاً فعلياً بها — مصدر مبدّل المؤسسة */
  institutions: InstitutionContext[];
  institutionId: string | null;
}

interface AuthContextValue extends AuthState {
  roles: EffectiveRole[];
  can: (p: Permission) => boolean;
  /** كلمةُ المرور: رموزٌ مباشرةً، أو تحدٍّ ينتظر الخطوةَ الثانية (0064) */
  /** `remember` = «ابقَ مسجّلاً على هذا الجهاز» (0068) — الخادمُ يقرّر إن كان للحساب أن يوثّق جهازاً */
  signIn: (email: string, password: string, remember?: boolean) => Promise<TokenPair | MfaChallenge>;
  /** `email` لحفظ رمز الجهاز الموثوق إن أعاده الخادم (0089) */
  completeMfa: (mfaToken: string, second: { code?: string; recoveryCode?: string }, remember?: boolean, email?: string) => Promise<TokenPair>;
  signOut: () => Promise<void>;
  switchInstitution: (id: string | null) => Promise<void>;
  reload: () => Promise<void>;
  /** اسمُ الحساب التجريبي الذي دخلته إدارة المنصة — `null` خارج ذلك */
  sandboxAs: string | null;
  /** إدارة المنصة تدخل حساباً تجريبياً بزرّ (21 سبتمبر 2026) */
  enterSandbox: (userId: string, label: string) => Promise<void>;
  /** العودة من الحساب التجريبي إلى جلسة إدارة المنصة */
  returnFromSandbox: () => Promise<void>;
  /** «عرضٌ بعين العضو» (0122): على من، وإلى متى — `null` خارج ذلك */
  viewAs: ViewAsState | null;
  enterViewAs: (memberId: string, reasonAr: string) => Promise<void>;
  exitViewAs: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // لا جلسة محفوظة ⇒ نبدأ مجهولين مباشرةً بلا وميض «جارٍ التحميل»
  const [state, setState] = useState<AuthState>(() => ({
    status: tokens.refresh ? 'loading' : 'anonymous',
    me: null, institutions: [], institutionId: institutionScope.get(),
  }));
  const [sandboxAs, setSandboxAs] = useState<string | null>(() => sandboxReturn.get()?.as ?? null);
  const [viewAs, setViewAs] = useState<ViewAsState | null>(() => viewAsStore.get());
  /**
   * ذاكرةُ الطلبات تُمسح عند كل تبديلٍ للهوية: بلا هذا يرى الحسابُ التجريبيّ
   * لحظةً ما خبّأته جلسةُ الإدارة تحت المفتاح نفسِه (`['me', …]` مثلاً)، والعكس.
   */
  const qc = useQueryClient();
  const navigate = useNavigate();

  /**
   * تسلسل الإقلاع:
   * 1) /auth/me بلا سياق → المستخدم ودوره على مستوى المنصة.
   * 2) /auth/me/institutions → السياقات المتاحة له فعلاً (المستفيد لا يخمّن معرّف مؤسسة أبداً).
   * 3) اختيار السياق: المحفوظ إن بقي صالحاً، وإلا الأول — ثم /auth/me مرة أخرى بالسياق
   *    للحصول على الدور الفعّال داخله. صاحب السياق الواحد لا يُطالَب باختيار شيء.
   */
  const load = useCallback(async (preferredInstitution?: string | null) => {
    const wanted = preferredInstitution === undefined ? institutionScope.get() : preferredInstitution;

    let base: MeResponse;
    try {
      base = await api.get<MeResponse>('/auth/me', { institutionId: null });
    } catch (e) {
      // الجلسةُ تُمسح حين يرفضها الخادمُ وحده (401/403) — انقطاعُ شبكةٍ أو عطلٌ عابر (5xx) عند فتح التطبيق كان يمحو جلسةً
      // محفوظةً لأسابيع فيُطلب الدخولُ كاملاً من جديد (17 أكتوبر 2026). نبقى مجهولين الآن، والجلسةُ تعود بإعادة التحميل.
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) tokens.clear();
      setState({ status: 'anonymous', me: null, institutions: [], institutionId: null });
      return;
    }

    let contexts: InstitutionContext[] = [];
    try {
      contexts = await api.get<InstitutionContext[]>('/auth/me/institutions', { institutionId: null });
    } catch { /* بعض الحسابات بلا مؤسسة أصلاً — إدارة المنصة والأخصائي قبل أول ارتباط */ }

    const chosen = contexts.some((c) => c.id === wanted) ? wanted : contexts[0]?.id ?? null;
    institutionScope.set(chosen);

    let me = base;
    if (chosen) {
      try {
        me = await api.get<MeResponse>('/auth/me', { institutionId: chosen });
      } catch {
        // السياق لم يعد صالحاً (أُنهي الارتباط مثلاً) — نعود لنطاق المنصة بدل تعليق المستخدم
        institutionScope.set(null);
      }
    }

    setState({ status: 'authenticated', me, institutions: contexts, institutionId: institutionScope.get() });
  }, []);

  useEffect(() => {
    // تحميل الهوية عند الإقلاع: load دالة غير متزامنة ولا تُحدِّث الحالة إلا بعد رد الخادم،
    // لكن القاعدة لا تستطيع رؤية ذلك عبر حدود async — التعطيل مقصود ومحصور بهذا السطر.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (tokens.refresh) void load();
  }, [load]);

  useEffect(() => onAuthLost((reason) => {
    // انتهاءُ عرضٍ بعين عضو (0122) عودةٌ إلى جلسة المدير لا خروج — يُعالَج بعد تعريف exitViewAs أدناه
    if (reason === 'view-as-ended') { window.dispatchEvent(new CustomEvent('rafrafa:view-as-ended')); return; }
    setAppBadge(0);
    setState({ status: 'anonymous', me: null, institutions: [], institutionId: null });
  }), []);

  const acceptPair = useCallback(async (pair: TokenPair) => {
    // دخولٌ بكلمة مرور يبدأ جلسةً جديدة — فلا «عودة» معلّقة من حسابٍ تجريبيٍّ سابق
    sandboxReturn.clear();
    sandboxVisit.clear();
    setSandboxAs(null);
    qc.clear();
    markActive(true);
    tokens.set(pair);
    if (!pair.mustChangePassword) await load(null);
    else setState((s) => ({ ...s, status: 'loading' }));
    return pair;
  }, [load, qc]);

  const signIn = useCallback(async (email: string, password: string, remember = false) => {
    // رمزُ الجهاز الموثوق لهذا البريد إن وُجد (0089) — الخادمُ يتخطّى به رمزَ التطبيق أو يتجاهله فيطلبه
    const deviceToken = trustedDevice.get(email);
    const r = await api.post<TokenPair | MfaChallenge>('/auth/login', { email, password, remember, ...(deviceToken ? { deviceToken } : {}) }, { anonymous: true, institutionId: null });
    if ('mfaRequired' in r && r.mfaRequired) return r;
    return acceptPair(r as TokenPair);
  }, [acceptPair]);

  const completeMfa = useCallback(async (mfaToken: string, second: { code?: string; recoveryCode?: string }, remember = false, email?: string) => {
    const pair = await api.post<TokenPair>('/auth/login/2fa', { mfaToken, ...second, remember }, { anonymous: true, institutionId: null });
    if (pair.deviceToken && pair.deviceTrustedUntil && email) trustedDevice.set(email, pair.deviceToken, pair.deviceTrustedUntil);
    return acceptPair(pair);
  }, [acceptPair]);

  /**
   * العودة إلى الإدارة: جلسةُ الحساب التجريبي تُنهى في الخادم أولاً، ثم يُدوَّر
   * رمزُ الإدارة المحفوظ. فإن تعذّر التدوير (انتهت الجلسة المحفوظة) يخرج المستخدم
   * إلى صفحة الدخول — لا يبقى عالقاً في حسابٍ تجريبيٍّ يظنّه حسابَه.
   */
  const returnFromSandbox = useCallback(async () => {
    const saved = sandboxReturn.get();
    try { await api.post('/auth/logout', { refreshToken: tokens.refresh ?? undefined }, { institutionId: null }); }
    catch { /* الخروج محلي حتى لو فشل الطلب */ }
    sandboxReturn.clear();
    setSandboxAs(null);
    tokens.clear();
    qc.clear();
    if (!saved) { setState({ status: 'anonymous', me: null, institutions: [], institutionId: null }); return; }
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const pair = await api.post<TokenPair>('/auth/refresh', { refreshToken: saved.refresh }, { anonymous: true, institutionId: null });
      tokens.set(pair);
      await load(saved.institutionId);
      // العودةُ إلى الصفحة التي دخل منها — لا إلى صفحةٍ من الحساب التجريبي لا صلاحيةَ له فيها
      navigate('/platform/sandbox', { replace: true });
    } catch {
      setState({ status: 'anonymous', me: null, institutions: [], institutionId: null });
    }
  }, [load, qc, navigate]);

  /**
   * عرضٌ بعين العضو (0122): رمزٌ قصير باسم العضو، للقراءة وحدها، بلا تحديث. جلسةُ المدير تُحفظ جانباً وتعود بالخروج —
   * ولا يُخطَر العضو (قرار صاحب المنصة)؛ الأثرُ في الخادم. `qc.clear()` عند كل تبديل: لا تبقى شاشةٌ من هويةٍ على ذاكرة الأخرى.
   */
  const exitViewAs = useCallback(async () => {
    const saved = viewAsStore.get() ?? (() => { try { const raw = localStorage.getItem('rafrafa.viewAs'); return raw ? (JSON.parse(raw) as ViewAsState) : null; } catch { return null; } })();
    try { await api.post(VIEW_AS_END_PATH, undefined, { institutionId: saved?.institutionId ?? null }); } catch { /* انتهى أصلاً */ }
    viewAsStore.clear();
    setViewAs(null);
    tokens.clear();
    qc.clear();
    if (!saved?.returnRefresh) { setState({ status: 'anonymous', me: null, institutions: [], institutionId: null }); return; }
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const pair = await api.post<TokenPair>('/auth/refresh', { refreshToken: saved.returnRefresh }, { anonymous: true, institutionId: null });
      tokens.set(pair);
      await load(saved.institutionId);
      navigate(`/app/staff/${saved.memberId}/work`, { replace: true });
    } catch {
      setState({ status: 'anonymous', me: null, institutions: [], institutionId: null });
    }
  }, [load, qc, navigate]);

  const enterViewAs = useCallback(async (memberId: string, reasonAr: string) => {
    const r = await api.post<{ accessToken: string; expiresAt: string; member: { id: string; fullName: string } }>(`/staff/${memberId}/view-as`, { reasonAr });
    const state: ViewAsState = { returnRefresh: tokens.refresh, institutionId: institutionScope.get(), memberId: r.member.id, memberName: r.member.fullName, until: r.expiresAt };
    viewAsStore.set(state);
    setViewAs(state);
    qc.clear();
    // رمزُ العرض وحده — بلا رمز تحديث (لا تدوير)
    tokens.set({ accessToken: r.accessToken, refreshToken: '' });
    setState((s) => ({ ...s, status: 'loading' }));
    await load(state.institutionId);
    navigate('/me/home', { replace: true });
  }, [load, qc, navigate]);

  useEffect(() => { const h = () => { void exitViewAs(); }; window.addEventListener('rafrafa:view-as-ended', h); return () => window.removeEventListener('rafrafa:view-as-ended', h); }, [exitViewAs]);

  const enterSandbox = useCallback(async (userId: string, label: string) => {    const pair = await api.post<TokenPair>(`/platform/sandbox-accounts/${userId}/enter`, undefined, { institutionId: null });
    const adminRefresh = tokens.refresh;
    // مدخلٌ ثانٍ من داخل حسابٍ تجريبي لا يحدث (الصلاحية للإدارة وحدها) — وإن حدث
    // فالمحفوظُ الأولُ يبقى: العودةُ دائماً إلى الإدارة لا إلى حسابٍ تجريبيٍّ سابق
    if (adminRefresh && !sandboxReturn.get()) {
      sandboxReturn.set({ refresh: adminRefresh, institutionId: institutionScope.get(), as: label });
    }
    setSandboxAs(label);
    qc.clear();
    tokens.set(pair);
    institutionScope.set(null);
    setState((s) => ({ ...s, status: 'loading' }));
    await load(null);
  }, [load, qc]);

  const signOut = useCallback(async () => {
    // الخروجُ من حسابٍ تجريبيٍّ دخلته الإدارة عودةٌ إليها لا خروجٌ من المنصة — وكذلك الخروجُ من عرضٍ بعين عضو
    if (viewAsStore.get()) { await exitViewAs(); return; }
    if (sandboxReturn.get()) { await returnFromSandbox(); return; }
    try { await api.post('/auth/logout', { refreshToken: tokens.refresh ?? undefined }, { institutionId: null }); }
    catch { /* الخروج محلي حتى لو فشل الطلب */ }
    tokens.clear();
    sandboxVisit.clear();
    qc.clear();
    setAppBadge(0); // عددُ غير المقروء لصاحب الجلسة لا يبقى على أيقونةٍ بعد خروجه
    setState({ status: 'anonymous', me: null, institutions: [], institutionId: null });
  }, [returnFromSandbox, exitViewAs, qc]);

  const switchInstitution = useCallback(async (id: string | null) => {
    setState((s) => ({ ...s, status: 'loading' }));
    await load(id);
  }, [load]);

  const roles = useMemo(() => state.me?.roles ?? [], [state.me]);
  const can = useCallback((p: Permission) => roleHasPermission(roles, p), [roles]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, roles, can, signIn, completeMfa, signOut, switchInstitution, reload: () => load(), sandboxAs, enterSandbox, returnFromSandbox, viewAs, enterViewAs, exitViewAs }),
    [state, roles, can, signIn, completeMfa, signOut, switchInstitution, load, sandboxAs, enterSandbox, returnFromSandbox, viewAs, enterViewAs, exitViewAs],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth خارج AuthProvider');
  return ctx;
}
